import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { useModule } from "@/hooks/use-module";
import { parseError } from "@/lib/parse-error";
import { userIsAdmin } from "@/lib/permissions";
import { toast } from "@/lib/use-toast";
import type { SetupChecklistResponse, SetupItemDetail } from "@/types/setup-checklist";
import { completeSetupItem, fetchSetupChecklist, finishSetupChecklist, skipSetupItem, uncompleteSetupItem, unskipSetupItem } from "./setup-checklist";
export const setupChecklistQueryKeys = {
  all: ["setup-checklist"] as const,
  list: () => [...setupChecklistQueryKeys.all, "list"] as const
};
export function useSetupChecklistAvailable() {
  const $ = _c(4);
  const {
    currentUser
  } = useUser();
  const {
    tenant
  } = useTenant();
  const hasModule = useModule("SETUP_CHECKLIST");
  let t0;
  if ($[0] !== currentUser || $[1] !== hasModule || $[2] !== tenant?.kind) {
    t0 = hasModule && userIsAdmin(currentUser) && tenant?.kind !== "MASTER";
    $[0] = currentUser;
    $[1] = hasModule;
    $[2] = tenant?.kind;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}
export function useSetupChecklist() {
  const $ = _c(3);
  const available = useSetupChecklistAvailable();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = setupChecklistQueryKeys.list();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== available) {
    t1 = {
      queryKey: t0,
      queryFn: fetchSetupChecklist,
      enabled: available
    };
    $[1] = available;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useQuery(t1);
}
type TransitionArgs = {
  itemId: string;
};
async function toastFailure(error: unknown) {
  toast({
    title: "Could not update the setup guide",
    description: await parseError(error),
    variant: "error"
  });
}

/** Recounts totals the same way the server does. */
function withItem(list: SetupChecklistResponse, item: SetupItemDetail): SetupChecklistResponse {
  const items = list.items.map(existing => existing.id === item.id ? {
    ...item,
    events: []
  } : existing);
  const open = items.filter(i => i.status === "OPEN");
  return {
    ...list,
    items,
    totals: {
      open: open.length,
      done: items.filter(i => i.status === "DONE").length,
      skipped: items.filter(i => i.status === "SKIPPED").length
    },
    criticalOpen: open.filter(i => i.definition.critical).length,
    finishedAt: open.length === 0 ? list.finishedAt : null
  };
}
function useSetupItemTransition(mutationFn) {
  const $ = _c(5);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = item => queryClient.setQueryData(setupChecklistQueryKeys.list(), list => list ? withItem(list, item) : list);
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== mutationFn || $[3] !== t0) {
    t1 = {
      mutationFn,
      onSuccess: t0,
      onError: toastFailure
    };
    $[2] = mutationFn;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
}
export const useCompleteSetupItem = () => {
  return useSetupItemTransition(_temp);
};
export const useUncompleteSetupItem = () => {
  return useSetupItemTransition(_temp2);
};
export const useSkipSetupItem = () => {
  return useSetupItemTransition(_temp3);
};
export const useUnskipSetupItem = () => {
  return useSetupItemTransition(_temp4);
};
function useFinishTransition(mutationFn) {
  const $ = _c(5);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = list => queryClient.setQueryData(setupChecklistQueryKeys.list(), list);
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== mutationFn || $[3] !== t0) {
    t1 = {
      mutationFn,
      onSuccess: t0,
      onError: toastFailure
    };
    $[2] = mutationFn;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
}
export const useFinishSetupChecklist = () => {
  return useFinishTransition(finishSetupChecklist);
};
function _temp(t0) {
  const {
    itemId
  } = t0;
  return completeSetupItem(itemId);
}
function _temp2(t0) {
  const {
    itemId
  } = t0;
  return uncompleteSetupItem(itemId);
}
function _temp3(t0) {
  const {
    itemId
  } = t0;
  return skipSetupItem(itemId);
}
function _temp4(t0) {
  const {
    itemId
  } = t0;
  return unskipSetupItem(itemId);
}